import { useSelector } from 'react-redux'

import styles from './MdmSetupStep.module.scss'

import Button from '@/components/Button'
import Checkbox, { CheckboxGroup } from '@/components/Checkbox'
import RadioButton from '@/components/RadioButton'
import { MDM_PROVIDER_OPTIONS, MDM_UPDATE_INTENT_OPTIONS } from '@/pages/app/Settings/mdmLabels'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { MdmProvider, MdmUpdateIntent } from '@/types'

interface MdmSetupStepProps {
  providers: MdmProvider[]
  updateIntent: MdmUpdateIntent | null
  isSaving: boolean
  onProvidersChange: (providers: MdmProvider[]) => void
  onUpdateIntentChange: (intent: MdmUpdateIntent) => void
  onContinue: () => void
  onCancel?: () => void
}

const MdmSetupStep = (props: MdmSetupStepProps) => {
  const { providers, updateIntent, isSaving, onProvidersChange, onUpdateIntentChange, onContinue, onCancel } = props
  const isAdmin = useSelector(selectIsAdmin)
  const canContinue = providers.length > 0 && updateIntent !== null

  const toggleProvider = (provider: MdmProvider) =>
    onProvidersChange(providers.includes(provider) ? providers.filter((p) => p !== provider) : [...providers, provider])

  return (
    <div className={styles.root}>
      <p className={styles.intro}>
        Select every MDM you use and how updates are handled. This helps your team if devices fall behind. You can
        change it later.
      </p>

      <fieldset className={styles.group}>
        <legend className={styles.legend}>MDM provider (select all that apply)</legend>
        <CheckboxGroup className={styles.providers}>
          {MDM_PROVIDER_OPTIONS.map((option) => (
            <Checkbox
              key={option.id}
              label={option.title}
              checked={providers.includes(option.id)}
              onChange={() => toggleProvider(option.id)}
              disabled={isSaving || !isAdmin}
            />
          ))}
        </CheckboxGroup>
      </fieldset>

      <div className={styles.group} role="radiogroup" aria-label="Update plan">
        <span className={styles.legend}>Update plan</span>
        {MDM_UPDATE_INTENT_OPTIONS.map((option) => (
          <RadioButton
            key={option.id}
            label={option.title}
            selected={updateIntent === option.id}
            disabled={isSaving || !isAdmin}
            className={styles.intentOption}
            onClick={() => onUpdateIntentChange(option.id)}
          >
            <span className={styles.helper}>{option.helperText}</span>
          </RadioButton>
        ))}
      </div>

      <div className={styles.actions}>
        <PermissionTooltip>
          <Button
            title="Continue"
            type="dark"
            onClick={onContinue}
            isDisabled={!canContinue || !isAdmin}
            isLoading={isSaving}
          />
        </PermissionTooltip>
        {onCancel && <Button title="Cancel" type="light" onClick={onCancel} isDisabled={isSaving} />}
      </div>
    </div>
  )
}

export default MdmSetupStep
